/**
 * quickBonusKinds.js
 *
 * Registry for the recognised activeQuickBonuses `type` identifiers.
 * Each entry encodes the mechanical contract for that kind — what it
 * affects, how it rolls, and how the value is applied.
 *
 * [Sub-plan 1Q-F-5b-ii — 2026-05-28]: Replaces 9 raw `b.type === '<kind>'`
 *   string-equality checks across App.jsx, EquipmentPanel.js,
 *   SkillsPanel.js, and effectCalculator.js (Rule 11 / Rule 17 retirement).
 *
 * CONSUMER CONTRACT:
 *   - Import { QUICK_BONUS_KINDS, findQuickBonusOfKind } from this file.
 *   - Use findQuickBonusOfKind(activeQuickBonuses, 'bless') instead of
 *     activeQuickBonuses.find(b => b.type === 'bless').
 *   - Use QUICK_BONUS_KINDS.acBonus.id (or QUICK_BONUS_KINDS.acBonus.affects)
 *     instead of the literal string 'acBonus' for guard checks.
 *
 * STORAGE CONTRACT (UNCHANGED):
 *   activeQuickBonuses[] entries are {id?, type, name, value?, ...} objects
 *   written by QuickBonusesPanel, potion appliers, and presets.
 *   This registry only changes how CONSUMERS read them — it does NOT change
 *   the write shape.
 *
 * EXTENSION (multi-system data-block engine):
 *   PF2e / Mouseguard / CoC custom buffs can register as additional kinds
 *   here and ride the same consumer path without touching any consumer file.
 */

export const QUICK_BONUS_KINDS = {
  rage: {
    id: 'rage',
    label: 'Rage',
    affects: ['damage', 'condition:raging'],
    rollDie: null,
    // [TASK 5 — 2026-05-31 GAP-4]: Rage context flag.
    // Written by ClassResourcesPanel when a Rage use is activated (updateResource
    // delta < 0 for resourceType 'rage'); removed on long rest / end.
    // No QB preset button — the panel preset was removed (spec 4.3 / Task 4).
    // effectCalculator._buildContext reads this to set context.raging.
    // [BREADCRUMB] rage damage bonus scaling (PHB p.49: +3 at L9, +4 at L16) —
    // shadow entry currently hardcodes value:2; level-correct scaling is deferred.
    operation: 'addNumeric',
  },
  bless: {
    id: 'bless',
    label: 'Bless',
    affects: ['attackRoll', 'savingThrow'],
    rollDie: '1d4',
    operation: 'addDice',
  },
  guidance: {
    id: 'guidance',
    label: 'Guidance',
    affects: ['abilityCheck'],
    rollDie: '1d4',
    operation: 'addDice',
  },
  bardicInspiration: {
    id: 'bardicInspiration',
    label: 'Bardic Inspiration',
    affects: ['attackRoll', 'abilityCheck', 'savingThrow'],
    // [TASK 3 — 2026-05-31 GAP-3]: Legacy reader convention preserved for old saves.
    // The QB Bardic Inspiration preset was removed (spec 4.2 / Task 3) because it
    // hardcoded value:'1d6' (wrong at L5+) and was disconnected from ClassResourcesPanel.
    // No NEW QB writer should create 'bardicInspiration' entries — Bard's BI die is
    // tracked in ClassResourcesPanel (classResources.js Bard.bardic_inspiration).
    // Old saves may have activeQuickBonuses entries with type:'bardicInspiration';
    // this kind registration keeps findQuickBonusOfKind functional for those entries
    // (the migration notice in QuickBonusesPanel will clear them on first render).
    rollDie: 'dynamic',
    operation: 'addDice',
  },
  acBonus: {
    id: 'acBonus',
    label: 'AC Bonus',
    affects: ['ac'],
    rollDie: null,
    // bonus.value is a plain number — NOT a dice string.
    operation: 'addNumeric',
  },
  // [Sub-plan 1H-H-cond GAP-8 — 2026-05-29]: Mode toggle for "saving throw vs spell".
  // When active, calculateSavingThrow stamps context.rollSourceType='vs_spells' so
  // items/feats with condition:{category:'rollSourceType',op:'hasAny',operands:['vs_spells']}
  // (e.g. staff_of_the_magi +2 to all saves vs spells) fire correctly.
  spellSaveSource: {
    id: 'spellSaveSource',
    label: 'Saving vs Spells',
    affects: ['rollSourceType'],
    rollDie: null,
    operation: 'setContext',
  },
};

/**
 * findQuickBonusOfKind
 *
 * Locate the first active quick bonus that matches `kindId`.
 *
 * Handles BOTH the legacy `b.id` and `b.type` fields (effectCalculator
 * L1665 previously used `b.id === 'bless' || b.type === 'bless'` because
 * some write-paths stamp `id` and others stamp `type`). This helper
 * preserves backward-compat for both shapes.
 *
 * @param {Array} activeQuickBonuses - array from playerStats / settings
 * @param {string} kindId - one of the QUICK_BONUS_KINDS keys (e.g. 'bless')
 * @returns {Object|undefined} the matching bonus entry, or undefined
 */
export function findQuickBonusOfKind(activeQuickBonuses, kindId) {
  return (activeQuickBonuses || []).find(
    b => b && (b.id === kindId || b.type === kindId)
  );
}
